<template>
  <div data-step="detailed-results" class="panel">
    <div class="panel-header">
      <ProgressCircle v-if="!isCompatibilityPending" :isNone="!compatibility" :percentage="compatibility * 100" :size="60" :stroke="8" :gapAngle="90" />
      <div class="panel-header-text">
        <h3>{{ talent?.candidate?.first_name }} {{ talent?.candidate?.last_name }} <span class="s-text" style="font-weight: 400;">({{ talent?.candidate?.email }})</span></h3>
        <div class="ponderation-block">
          <p>{{ $t('app.campaign.result.panel.description') }}</p>
        </div>
      </div>
      <div class="right-buttons">
        <div data-step="detailed-results-list" class="display-mode-button-group">
          <Button
            :class="{'primary': appStore.talentPanelDisplayMode === DisplayMode.LIST, 'outline-white': appStore.talentPanelDisplayMode === DisplayMode.GRAPH}"
            class="square list-display-button xs" v-tooltip="$t('app.campaign.result.panel.listViewButton')"
            @click="appStore.talentPanelDisplayMode = DisplayMode.LIST"
          >
            <template #prefix>
              <ListIcon style="width: 14px"></ListIcon>
            </template>
          </Button>
          <Button v-if="!isCampaignV1(currentCampaign)"
            :class="{'primary': appStore.talentPanelDisplayMode === DisplayMode.GRAPH, 'outline-white': appStore.talentPanelDisplayMode === DisplayMode.LIST}"
            class="square graph-display-button xs" v-tooltip="$t('app.campaign.result.panel.graphViewButton')"
            @click="appStore.talentPanelDisplayMode = DisplayMode.GRAPH"
          >
            <template #prefix>
              <GraphIcon style="width: 14px"></GraphIcon>
            </template>
          </Button>
        </div>
        <Button v-if="!isCampaignV1(currentCampaign)" class="square outline-white manual-book-button xs" v-tooltip="$t('app.campaign.result.panel.manualButton')" @click="downloadEvalManualPdf">
          <template #prefix>
            <BookIcon style="width: 14px"></BookIcon>
          </template>
        </Button>
        <Button class="square outline-white return-button xs" v-tooltip="$t('app.campaign.result.panel.returnButton')" @click="emits('closeTalentPanel')" >
          <template #prefix>
            <ReturnIcon style="width: 14px"></ReturnIcon>
          </template>
        </Button>
      </div>
    </div>
    <div data-step="detailed-results-graph" class="parent">
      <div v-if="appStore.talentPanelDisplayMode === DisplayMode.LIST || isCampaignV1(currentCampaign)" class="result-card skills-container">
        <div class="skills-header">
          <span class="result-card-title">{{ $t('app.campaign.result.panel.skills.title') }}</span>
          <div class="social-desirability-warning" :class="{detected: hasSocialDesirability}" v-tooltip="hasSocialDesirability ? $t('app.campaign.candidatePanel.socialDesirabilityDetected') : $t('app.campaign.candidatePanel.socialDesirabilityNotDetected')">
            <SocialDesirabilityIcon style="width: 12px"></SocialDesirabilityIcon>
          </div>
        </div>
        <div class="skill-scores">
          <SkillSliderBar v-for="score in scores" :score="{skill: score[0], score: (score[1] as number * 100)}" :minMax="participationsStats?.minMaxScores" :isV1="isCampaignV1(currentCampaign)"></SkillSliderBar>
        </div>
      </div>
      <div v-if="!isCampaignV1(currentCampaign) && appStore.talentPanelDisplayMode === DisplayMode.GRAPH && (campaignAverageScores && Object.keys(campaignAverageScores).length >= 3)" class="result-card v2-graph">
        <div class="center-graph">
          <PositioningChart v-if="talent?.participant_id" :darkmode="isDark" :assessedSkills="skillsEvaluated" :participant="talent" :averageScores="campaignAverageScores" />
        </div>
      </div>
      <div v-if="!isCampaignV1(currentCampaign)" class="result-card styles-container">
        <span class="result-card-title">{{ $t('app.campaign.result.panel.styles.title') }}</span>
        <div class="style-wrapper">
          <TalentStyles :talent="talent" :detailedPourcentageView="appStore.talentPanelDisplayMode === DisplayMode.GRAPH"></TalentStyles>
        </div>
      </div>
      <div v-if="!isCampaignV1(currentCampaign) && appStore.talentPanelDisplayMode === DisplayMode.LIST" class="result-card badges-container">
        <div class="badges-header">
          <span class="result-card-title">{{ $t('app.campaign.result.panel.success.title') }}</span>
          <InfoIcon style="width: 14px; margin-bottom: -1px;" v-tooltip="$t('app.campaign.result.panel.success.infoTooltip')"></InfoIcon>
        </div>
        <Badges :talent="talent"></Badges>
      </div>
      <div v-if="isCampaignV1(currentCampaign)" class="result-card v1-chart">
        <PositioningChart v-if="talent?.participant_id" :darkmode="isDark" :assessedSkills="skillsEvaluated" :participant="talent" :averageScores="campaignAverageScores" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export enum DisplayMode {
  LIST = 'LIST',
  GRAPH = 'GRAPH',
};
</script>

<script setup lang="ts">
import moment from "moment";
import { reactive, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useDark } from "@vueuse/core";
import { useQuery } from "@tanstack/vue-query";

import { Campaign, WeightingModule } from "@/types/campaign";

import VField from "@/components/global/VField.vue";
import PositioningChart from "@/components/charts/custom/PositioningChart.vue";
import SkillSliderBar from "@/components/campaigns/SkillSliderBar.vue";
import Badges from "@/components/campaigns/Badges.vue";
import TalentStyles from "@/components/campaigns/TalentStyles.vue";
import ProgressCircle from "@/components/global/ProgressCircle.vue";

import evalManualPdfUrl from '@/assets/pdf/Manuel des évaluations de Yuzu.pdf';
import evalManualPdfEnUrl from '@/assets/pdf/Yuzu assessment manual.pdf';

import InfoIcon from '@/assets/images/icon/campaign/result_panel/info.svg?component';
import SocialDesirabilityIcon from '@/assets/images/icon/campaign/result_panel/social_desirability.svg?component';
import ListIcon from '@/assets/images/icon/campaign/result_panel/list.svg?component';
import GraphIcon from '@/assets/images/icon/campaign/result_panel/graph.svg?component';
import BookIcon from '@/assets/images/icon/campaign/result_panel/book.svg?component';
import ReturnIcon from '@/assets/images/icon/campaign/result_panel/return.svg?component';
import SendIcon from '@/assets/images/icon/campaign/result_panel/send.svg?component';

import { Participant } from "@/types/participant";
import { Note } from "@/types/participationV1";
import { participationsApi } from "@/api/participations.api";
import { useCommentParticipation, useParticipationsStats } from "@/queries/participant.query";
import { useCampaign } from "@/queries/campaigns.query";
import { useUser } from "@/queries/user.query";

const { locale, t } = useI18n();
const toast = useToast();
const isDark = useDark();

const route = useRoute();

const { getCampaignEvaluatedSkills, isCampaignV1 } = useSkillsStore();
const appStore = useAppStore();

const state = reactive<{
  displayMode: DisplayMode,
  inputNote: string,
  notes: Note[],
  simulatedPonderation: {
    required: WeightingModule[],
    primary: WeightingModule[],
    secondary: WeightingModule[],
    ignored: WeightingModule[],
  } | undefined,
}>({
  displayMode: DisplayMode.LIST,
  inputNote: "",
  notes: [] as Note[],
  simulatedPonderation: undefined,
});

const participationContext = computed(() => {
  return {
    organisationId: route.params.companyId as string,
    spaceId: route.params.spaceId as string,
    campaignId: route.params.campaignId as string,
  };
});

const { data: user } = useUser();
const { data: participationsStats, isPending: isPendingStats } = useParticipationsStats();
const { data: currentCampaign, isPending: isPendingCampaign } = useCampaign();

const currentCampaignWithSimulatedWeighting = computed(() => {
  if (!state?.simulatedPonderation) return currentCampaign.value;
  let currentCampaignCopy = structuredClone(toRaw(currentCampaign.value));
  currentCampaignCopy.assessment.weighting = { ...state.simulatedPonderation };
  return currentCampaignCopy;
})

const { data: compatibility, isPending: isCompatibilityPending } = useQuery({
  queryKey: ['compatibility', computed(() => talent?.participant_id), computed(() => currentCampaignWithSimulatedWeighting.value?.assessment?.weighting)],
  queryFn: () => participationsApi.getParticipationsCompatibility(talent, currentCampaignWithSimulatedWeighting.value),
  enabled: computed(() => !!talent?.results && !isPendingCampaign.value),
});

const { mutate: commentParticipation } = useCommentParticipation();

const emits = defineEmits(["closeTalentPanel", "talentUpdate"]);

const { talent } = defineProps<{
  talent?: Participant,
}>();

function formatDate(isoFormatDateString: string) {
  moment.locale(locale.value);
  const formattedDate = moment(isoFormatDateString).format("MMMM Do");
  return (formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1));
}

function downloadEvalManualPdf() {
  if (locale.value === 'fr') {
    window.open(evalManualPdfUrl, '_blank');
  } else {
    window.open(evalManualPdfEnUrl, '_blank');
  }
}

async function handleSend() {
  if (!state.inputNote.trim().length) return null;

  const now = new Date();
  state.notes.push({
    first_name: user?.value?.personal_information.first_name || "",
    last_name: user?.value?.personal_information.last_name || "",
    comment: state.inputNote.trim(),
    creation_date: now.toISOString(),
    isOldComment: false,
  });
  const notesJsonString = JSON.stringify(state.notes);

  if (notesJsonString.length > 2000) {
    toast.error(t('app.campaign.result.panel.notes.tooMuchNotesToast'));
    state.notes.pop();
    state.inputNote = "";
    return;
  }


  await saveNote(notesJsonString);
  
  state.inputNote = "";
  emits('talentUpdate');
}

async function saveNote(notesJsonString: string) {
  commentParticipation({ context: participationContext.value, participantId: talent.participant_id, comment: notesJsonString }, {
    onSuccess: () => {
      talent.user_comment = notesJsonString;
    },
    onError: (error) => {
      toast.error(error);
    }
  });
}

const scores = computed(() => {
  if (!talent?.results) return [];
  const filteredScores = Object.entries(talent?.results.scores).filter(([m, score]) => score !== null && m !== 'leadership' && m !== 'social_desirability');

  return filteredScores
    .sort(([mA, _], [mB, __]) => {
      const idA = t(`skills.${mA}.name`);
      const idB = t(`skills.${mB}.name`);
      return idA.localeCompare(idB);
    });
});

const hasSocialDesirability = computed(() => {
  return talent?.results?.scores.social_desirability > 0.75;
});

const skillsEvaluated = computed(() => {
  if (!talent?.results) return [];

  return getCampaignEvaluatedSkills(currentCampaign.value);
});

const campaignAverageScores = computed(() => {
  return participationsStats?.value?.averageScores;
});

watch(
  () => talent,
  (newVal, oldVal) => {
    if (!newVal?.user_comment || !newVal?.user_comment.length) {
      state.notes = [];
      return;
    }

    try {
      let commentsJson: Note[] = JSON.parse(newVal.user_comment);
      state.notes = commentsJson;
    } catch {
      state.notes = [{
        first_name: 'Global',
        last_name: 'comment',
        comment: newVal.user_comment,
        isOldComment: true,
        creation_date: '',
      }];
    }
  }
)

onMounted(() => {
  if (!talent?.user_comment) {
    state.notes = [];
    return;
  }

  try {
    let commentsJson: Note[] = JSON.parse(talent.user_comment);
    state.notes = commentsJson;
  } catch {
    state.notes = [{
      first_name: 'Global',
      last_name: 'comment',
      comment: talent.user_comment,
      isOldComment: true,
      creation_date: '',
    }];
  }
});

</script>

<style lang="scss" scoped>
  @import "@/assets/style/variables.scss";
  @import "@/assets/style/darkmode.scss";

  $little-screen: 1340px;

  .hidden {
    display: none !important;
  }

  .weight-simulation-title {
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .weight-simulation-desc {
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .panel {
    flex: 1;

    display: flex;
    flex-direction: column;
    padding: 24px;
    width: 100%;
    border: 1px solid var(--light-grey-color);
    border-radius: 0 0 12px 12px;
    background-color: var(--white-color);

    .panel-header {
      display: flex;
      align-items: center;
      padding-bottom: 12px;
      gap: 16px;

      .panel-header-text {
        h3 {
          color: var(--secondary-color);
          font-family: $bioSans;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
          line-height: 100%; /* 18px */
        }

        p {
          color: var(--secondary-color);
          font-family: $bioSans;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }

        .ponderation-block {
          display: flex;
          flex-direction: row;
          gap: 8px;
          align-items: center;

          color: var(--secondary-color);
          font-family: $bioSans;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;

          .ponderation-button {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }

      .right-buttons {
        margin-left: auto;

        display: flex;
        flex-direction: row;
        gap: 8px;

        .display-mode-button-group {
          display: flex;
          justify-content: center;
          align-items: center;
  
          .list-display-button {
            border-radius: 4px 0 0 4px;
            border: 1px solid var(--light-grey-color);
            border-right: 0;
          }
  
          .graph-display-button {
            border-radius: 0 4px 4px 0;
            border: 1px solid var(--light-grey-color);
          }
        }
  
        .manual-book-button {
          border-radius: 4px;
          border: 1px solid var(--light-grey-color);
        }
  
        .return-button {
          border-radius: 4px;
          border: 1px solid var(--light-grey-color);
        }
      }
      }

  }

  .input-container {
    display: flex;
    flex-direction: column;
    padding-top: 24px;
    gap: 8px;
  }

  .input {
    height: min-content;
  }

  .parent {
    flex: 1;
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;

    gap: 12px;
    overflow: hidden;

    @media (max-width: $little-screen) {
      flex-direction: row;
      flex-wrap: wrap;
    }
  
    .info-and-notes {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: 25%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
      height: 100%;

      @media (max-width: $little-screen) {
        flex-basis: calc(50% - 8px);
        height: 450px;
      }
    }

    .notes {
      display: flex;
      flex-direction: column;
      flex: 1;

      .notes-container {
        flex: 1;

        margin-top: 12px;
        gap: 12px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      
        .comments {
          flex-grow: 1;
          flex-basis: 0;

          display: flex;
          flex-direction: column;
          gap: 16px;
          padding-right: 15px;
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;

          &::-webkit-scrollbar {
            padding-left: 4px;
            width: 3px;
            border-radius: 24px;
            background-color: var(--light-grey-color);
          }

          &::-webkit-scrollbar-thumb {
            background: $primary-color;
            padding: 4px;
            width: 3px;
            border-radius: 24px;
          }

          .no-comment {
            color: var(--grey-50-color);
            font-family: $bioSans;
            font-size: 13px;
            font-style: italic;
            font-weight: 400;
            line-height: normal;
          }

          .note {
            color: var(--secondary-color);
            font-family: $bioSans;
            font-size: 13px;
            font-weight: 400;
            line-height: normal;
            text-wrap: wrap;

            .comment-info {
              display: flex;
              flex-direction: row;
              align-items: center;
              gap: 6px;

              .name {
                color: var(--secondary-color);
                font-family: $bioSans;
                font-size: 14px;
                font-weight: 600;
              }
  
              .date {
                color: $secondary-100-color;
                font-family: $bioSans;
                font-size: 12px;
                font-weight: 400;
              }
  
              .separator {
                border-top: 1px solid var(--secondary-color);
                flex-grow: 100;
                height: 1px;
                align-self: center;
              }
            }

            .comment-text {
              white-space: pre-line;
              flex-wrap: wrap;
              word-wrap: break-word;
              text-wrap: wrap;
            }
          }
        }
      }
    }

    .skills-container {
      flex-grow: 1;
      flex-basis: 25%;
      display: flex;
      flex-direction: column;
      gap: 24px;
      overflow: visible !important;
      position: relative;
      padding-left: 24px !important;

      @media (max-width: $little-screen) {
        flex-basis: calc(50% - 8px);
        height: 450px;
      }

      .skills-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .social-desirability-warning {
          display: flex;
          width: 24px;
          height: 24px;
          justify-content: center;
          align-items: center;
          gap: 10px;
          aspect-ratio: 1/1;
          border-radius: 3px;
          border: 1px solid var(--secondary-color);
          background: var(--light-grey-color);
          color: var(--grey-50-color);

          i {
            font-size: 10px;
            color: var(--grey-50-color);
          }

          &.detected {
            border: 1px solid $secondary-color;
            background: $primary-color;
            color: $secondary-color;

            i {
              color: $secondary-color;
            }
          }
        }
      }

      .skill-scores {
        flex-grow: 1;
        flex-basis: 0;
        overflow-y: auto;
        overflow-x: visible !important;

        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 32px;
        padding-right: 15px;

        &::-webkit-scrollbar {
          padding-left: 4px;
          width: 3px;
          border-radius: 24px;
          background-color: var(--light-grey-color);
        }

        &::-webkit-scrollbar-thumb {
          background: $primary-color;
          padding: 4px;
          width: 3px;
          border-radius: 24px;
        }
      }
    }

    .styles-container {
      flex-grow: 1;
      flex-shrink: 1;
      display: flex;
      flex-direction: column;
      flex-basis: 25%;
      //width: 100%;

      @media (max-width: $little-screen) {
        flex-basis: calc(50% - 8px);
        height: 450px;
      }
  
      .style-wrapper {
        flex-grow: 1;
        flex-basis: 0;
        overflow-y: auto;

        margin-top: 24px;
        padding-right: 15px;
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 24px;

        &::-webkit-scrollbar {
          padding-left: 4px;
          width: 3px;
          border-radius: 24px;
          background-color: var(--light-grey-color);
        }

        &::-webkit-scrollbar-thumb {
          background: $primary-color;
          padding: 4px;
          width: 3px;
          border-radius: 24px;
        }
      }
    }

    .badges-container {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 25%;

      @media (max-width: $little-screen) {
        flex-basis: calc(50% - 8px);
        height: 450px;
      }

      display: flex;
      flex-direction: column;

      .badges-header {
        display: flex;
        gap: 8px;
        align-items: center;

        i::before {
          font-weight: 600 !important;
        }
      }

      .badges-wrapper {
        padding-top: 24px;
      }
    }

    .v1-chart {
      flex-grow: 1;
      width: 32%;
    }

    .v2-graph {
      display: flex;
      flex-direction: column;
      flex-basis: 50%;

      .center-graph {
        display: flex;
        flex-direction: column;
        flex-basis: 70%;
        margin-top: auto;
        margin-bottom: auto;

        :deep(.customLegend) {
          margin-top: 44px;
        }
      }
    }

    .result-card {
      border: 1px solid var(--light-grey-color);
      border-radius: 0;
      padding: 16px;

      .result-card-title {
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 14px;
        font-weight: 700;
      }
    }
  }
</style>